import { useCallback, useMemo, useState } from 'react';

import { TableRow } from '@mui/material';
import { type Row } from '@tanstack/react-table';
import cx from 'classnames';

import { BACKGROUND_GREY } from 'theme/colors';

import EnhancedCell from './EnhancedCell';
import { responsiveRowStyles } from './styles';

interface EnhancedRowProps {
  row: Row<any>;
  style?: any;
  className?: string;
  isEditable?: boolean;
  isClickable?: boolean;
  resizingEnabled?: boolean;
  onRowClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: Row<any>, rowIndex?: number) => void;
}

const EnhancedRow = ({
  row,
  style,
  className,
  isEditable = false,
  isClickable,
  resizingEnabled = false,
  onRowClick,
}: EnhancedRowProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const setHoverRow = useCallback((state: boolean) => {
    setIsHovered(state);
  }, []);

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
      onRowClick?.(event, row);
    },
    [onRowClick, row],
  );

  const rowStyles = useMemo(() => {
    return {
      ...style,
      ...(row.getCanExpand() ? { background: BACKGROUND_GREY } : {}),
      cursor: (isClickable ?? onRowClick) ? 'pointer' : undefined,
    };
  }, [isClickable, onRowClick, row, style]);

  return (
    <TableRow key={row.id} className={cx(className, responsiveRowStyles())} style={rowStyles} onClick={handleRowClick}>
      {row.getVisibleCells().map((cell, index) => (
        <EnhancedCell
          key={cell.id}
          cell={cell}
          index={index}
          isRowEditable={isEditable}
          isRowExpandable={row.getCanExpand()}
          isRowHovered={isHovered}
          resizingEnabled={resizingEnabled}
          toggleHoverRow={setHoverRow}
        />
      ))}
    </TableRow>
  );
};

export default EnhancedRow;
